import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, isEmail, isPhone } from '@kss/ui';
import { ArrowLeft, KeyRound, Loader2, LogIn, Mail, Phone, ShieldCheck } from 'lucide-react';
import { portalApi } from './portalApi';
import { setPortalSession } from './portalSession';

/**
 * Customer portal sign-in.
 *
 * Two-step OTP flow:
 *   1. enter email / phone + tenant code → POST /auth/send-otp
 *   2. enter the 6-digit OTP            → POST /auth/verify-otp
 *
 * Verified response is unpacked into a portal-only session (separate
 * from the staff Redux auth) and the customer lands on the dashboard.
 */

interface AuthResponse {
  accessToken: string;
  refreshToken?: string;
  expiresAt: string;
  user?: { id?: number | string; username?: string; email?: string; phone?: string };
  tenant?: { tenantId: string; code: string; name?: string };
}

const inputCls =
  'h-10 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-sm text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)]';
const otpCls =
  'h-12 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card)] px-3 text-center text-2xl font-semibold tracking-[0.4em] text-[var(--color-text-primary)] outline-none focus:border-[var(--color-primary)]';

export default function PortalLogin() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<'identifier' | 'otp'>('identifier');
  const [identifier, setIdentifier] = useState('');
  const [tenantCode, setTenantCode] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const otpRef = useRef<HTMLInputElement>(null);

  // OTP resend countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = window.setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [resendIn]);

  // Auto-focus OTP field when stage 2 opens.
  useEffect(() => {
    if (stage === 'otp') otpRef.current?.focus();
  }, [stage]);

  function validateIdentifier(): string | null {
    const v = identifier.trim();
    if (!v) return 'Enter your email or mobile.';
    if (v.includes('@')) return isEmail(v);
    return isPhone(v);
  }

  async function sendOtp() {
    const e = validateIdentifier();
    if (e) { setErr(e); return; }
    if (!tenantCode.trim()) { setErr('Tenant code is required.'); return; }
    setBusy(true);
    setErr(null);
    try {
      // Per BACKEND_AGENT_PROMPTS.md §14.1 (shipped 2026-05-14): the
      // canonical send-otp body is `{ tenantCode, identifier }`. Extra
      // fields are tolerated by the JSON deserializer and ignored
      // server-side, so passing `purpose` / `tenantId` is harmless.
      const payload = {
        tenantCode,
        identifier: identifier.trim(),
        purpose: 'Login',
        tenantId: tenantId ?? tenantCode,
      };
      try {
        // Canonical lowercase path per §14.1.
        const r = await portalApi.post<{ tenantId?: string; expiresAt?: string }>(
          '/auth/send-otp', payload, { anonymous: true },
        );
        if (r?.tenantId) setTenantId(r.tenantId);
      } catch (e) {
        // Legacy capitalised path — older deployments may still serve this.
        await portalApi.post('/Auth/send-otp', payload, { anonymous: true });
      }
      setStage('otp');
      setResendIn(45);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not send OTP. Check the email / mobile and try again.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp() {
    if (otpCode.length !== 6) { setErr('Enter the 6-digit code we sent.'); return; }
    setBusy(true);
    setErr(null);
    try {
      // Per §14.1: verify-otp wants `{ tenantCode, identifier, code }`.
      // The previous shape sent `otpCode` which the server doesn't read
      // — verification silently failed. Field renamed to `code` to
      // match the live contract. `otpCode` kept as an alias for any
      // older backend builds.
      const payload = {
        tenantCode,
        identifier: identifier.trim(),
        code: otpCode,
        otpCode, // alias for legacy builds — ignored by the current API
        tenantId: tenantId ?? tenantCode,
      };
      let r: AuthResponse;
      try {
        // Canonical lowercase path per §14.1.
        r = await portalApi.post<AuthResponse>('/auth/verify-otp', payload, { anonymous: true });
      } catch {
        r = await portalApi.post<AuthResponse>('/Auth/verify-otp', payload, { anonymous: true });
      }
      if (!r?.accessToken) throw new Error('No access token returned.');
      setPortalSession({
        accessToken: r.accessToken,
        refreshToken: r.refreshToken,
        expiresAt: r.expiresAt,
        customerId: String(r.user?.id ?? ''),
        customerName: String(r.user?.username ?? identifier),
        email: r.user?.email,
        phone: r.user?.phone,
        tenantCode: String(r.tenant?.code ?? tenantCode),
        tenantId: String(r.tenant?.tenantId ?? tenantId ?? tenantCode),
      });
      navigate('/portal');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'OTP rejected — try again or request a new one.');
    } finally {
      setBusy(false);
    }
  }

  const isPhoneId = identifier.includes('@') ? false : /^\d/.test(identifier.trim());

  return (
    <div className="min-h-screen bg-[var(--color-bg-app)] text-[var(--color-text-primary)]">
      <div className="mx-auto flex min-h-screen max-w-md flex-col px-4 py-10 sm:px-6">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mb-6 inline-flex w-fit items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        >
          <ArrowLeft size={14} /> Back to home
        </button>

        <div className="mb-4">
          <h1 className="text-2xl font-semibold tracking-tight">Customer portal</h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
            View your invoices, download PDFs, and pay outstanding balances.
          </p>
        </div>

        {err && <Alert variant="danger" className="mb-3">{err}</Alert>}

        <Card>
          {stage === 'identifier' ? (
            <form
              onSubmit={(e) => { e.preventDefault(); void sendOtp(); }}
              className="space-y-3"
            >
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                  Email or mobile <span className="text-[var(--color-danger)]">*</span>
                </span>
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]">
                    {isPhoneId ? <Phone size={13} /> : <Mail size={13} />}
                  </span>
                  <input
                    autoFocus
                    className={`${inputCls} pl-8`}
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@company.com or 98XXXXXXXX"
                  />
                </div>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-[var(--color-text-secondary)]">Tenant code</span>
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]">
                    <ShieldCheck size={13} />
                  </span>
                  <input
                    className={`${inputCls} pl-8`}
                    value={tenantCode}
                    onChange={(e) => setTenantCode(e.target.value)}
                    placeholder="TNT-0001"
                  />
                </div>
                <span className="text-[11px] text-[var(--color-text-muted)]">
                  Find it on any invoice you received from your supplier.
                </span>
              </label>
              <Button type="submit" disabled={busy} className="mt-2 w-full">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                {busy ? 'Sending OTP…' : 'Send OTP'}
              </Button>
              <p className="text-[11px] text-[var(--color-text-muted)]">
                We&rsquo;ll send a 6-digit code to your inbox / phone — valid for 10 minutes.
              </p>
            </form>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); void verifyOtp(); }}
              className="space-y-3"
            >
              <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-card-hover)] px-3 py-2 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[var(--color-text-secondary)]">Sent to</span>
                  <button
                    type="button"
                    onClick={() => { setStage('identifier'); setOtpCode(''); }}
                    className="text-[11px] text-[var(--color-primary)] hover:underline"
                  >
                    Change
                  </button>
                </div>
                <div className="font-medium">{identifier} · {tenantCode}</div>
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-[var(--color-text-secondary)]">
                  6-digit code <span className="text-[var(--color-danger)]">*</span>
                </span>
                <input
                  ref={otpRef}
                  inputMode="numeric"
                  maxLength={6}
                  className={otpCls}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="••••••"
                />
              </label>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-[var(--color-text-muted)]">Code expires in 10 min.</span>
                <button
                  type="button"
                  onClick={() => { if (resendIn === 0) void sendOtp(); }}
                  disabled={resendIn > 0 || busy}
                  className="text-[var(--color-primary)] hover:underline disabled:opacity-50 disabled:no-underline"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend OTP'}
                </button>
              </div>
              <Button type="submit" disabled={busy || otpCode.length !== 6} className="mt-2 w-full">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <LogIn size={14} />}
                {busy ? 'Verifying…' : 'Sign in'}
              </Button>
            </form>
          )}
        </Card>

        <p className="mt-6 text-center text-[11px] text-[var(--color-text-muted)]">
          Are you a staff user instead?{' '}
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('kss:open-login', { detail: { tab: 'signin' } }))}
            className="text-[var(--color-primary)] hover:underline"
          >
            Sign in to the staff panel
          </button>
        </p>
      </div>
    </div>
  );
}
